import { DateTime } from "luxon";
import type { DayAhead } from "./api";

/**
 * The days ahead in words, for the strip on the Schedule page and the card in Settings.
 * A duty manager has one question of next week: is it planned, and does everything have
 * a stand. The strip answers that in a line and names the day when it does not.
 */

/** The marker the server puts in projectedFrom when CopOpt's schedule supplied the day. */
export const FROM_COPOPT = "copopt";

export const shortDay = (date: string, tz: string) => DateTime.fromISO(date, { zone: tz }).toFormat("ccc d LLL");

/** "feed 59 + CopOpt 31", or "projected from Tue 6 Oct" when that is all the day has. */
export function sourceMix(d: DayAhead, tz: string): string {
  const parts: string[] = [];
  if (d.sources.feed) parts.push(`feed ${d.sources.feed}`);
  if (d.sources.copopt) parts.push(`CopOpt ${d.sources.copopt}`);
  if (d.sources.projected) parts.push(parts.length || !d.projectedFrom || d.projectedFrom === FROM_COPOPT ? `projected ${d.sources.projected}` : `projected from ${shortDay(d.projectedFrom, tz)}`);
  if (d.sources.built) parts.push(`built here ${d.sources.built}`);
  return parts.length ? parts.join(" + ") : "nothing yet";
}

/**
 * Whether a day ahead needs a planner's eye: something has no stand, or the day is thin.
 * Thin is the server's word: it knows whether the counts are the whole day or one
 * airline's flights, and four WestJet flights are not a thin day.
 */
export const needsAttention = (d: DayAhead) => d.unallocated > 0 || d.thin;

export function daysAheadSummary(days: DayAhead[], tz: string): { text: string; tone: "ok" | "warn" } {
  const planned = days.filter((d) => d.planned).length;
  const head = `Next ${days.length} days: ${planned} planned`;
  const problems: string[] = [];
  for (const d of days) {
    if (d.unallocated > 0) problems.push(`${d.unallocated} ${d.unallocated === 1 ? "flight" : "flights"} without a stand on ${shortDay(d.date, tz)}`);
    else if (d.thin) problems.push(`only ${d.flights} ${d.flights === 1 ? "flight" : "flights"} on ${shortDay(d.date, tz)}`);
  }
  if (!problems.length) return { text: `${head}, every flight has a stand`, tone: "ok" };
  return { text: `${head}, ${problems.join(", ")}`, tone: "warn" };
}
